import React, { useState, useRef, useEffect } from 'react'
import { Camera, X, RotateCw, Check } from 'lucide-react'
import { Button } from './ui/button'
import { toast } from 'sonner'

const WebcamCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const [stream, setStream] = useState(null)
  const [capturedImage, setCapturedImage] = useState(null)
  const [devices, setDevices] = useState([])
  const [selectedDeviceId, setSelectedDeviceId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    startCamera()
    return () => {
      stopCamera()
    }
  }, [selectedDeviceId])

  const startCamera = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Get list of video devices
      const deviceList = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = deviceList.filter(device => device.kind === 'videoinput')
      setDevices(videoDevices)

      // Use selected device or default
      const constraints = {
        video: selectedDeviceId 
          ? { deviceId: { exact: selectedDeviceId } }
          : { facingMode: 'environment' }
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      setStream(mediaStream)

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }

      setIsLoading(false)
    } catch (err) {
      console.error('Camera error:', err)
      setError('Failed to access camera. Please ensure camera permissions are granted.')
      setIsLoading(false)
      toast.error('Camera access denied. Please check permissions.')
    }
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw video frame to canvas
    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.9)
    setCapturedImage(imageData)
  }

  const handleRetake = () => {
    setCapturedImage(null)
  }

  const handleUsePhoto = () => {
    if (capturedImage) {
      // Remove data:image/jpeg;base64, prefix
      const base64Data = capturedImage.split(',')[1]
      onCapture(base64Data, capturedImage)
      stopCamera()
      onClose()
    }
  }

  const switchCamera = () => {
    if (devices.length > 1) {
      const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId)
      const nextIndex = (currentIndex + 1) % devices.length
      setSelectedDeviceId(devices[nextIndex].deviceId)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" data-testid="webcam-capture">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full">
        {/* Header */}
        <div className="bg-gray-900 text-white p-4 flex justify-between items-center rounded-t-lg">
          <h2 className="text-lg font-semibold">
            {capturedImage ? 'Review Photo' : 'Capture Photo'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              stopCamera()
              onClose()
            }}
            className="text-white hover:bg-gray-800"
            data-testid="close-webcam"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Camera/Preview Area */}
        <div className="relative bg-black" style={{ height: '500px' }}>
          {error ? (
            <div className="flex items-center justify-center h-full text-white">
              <div className="text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg mb-2">Camera Access Required</p>
                <p className="text-sm text-gray-400">{error}</p>
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center h-full text-white">
              <div className="text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 animate-pulse" />
                <p>Starting camera...</p>
              </div>
            </div>
          ) : capturedImage ? (
            <img 
              src={capturedImage} 
              alt="Captured" 
              className="w-full h-full object-contain"
              data-testid="captured-preview"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-contain"
              data-testid="video-feed"
            />
          )}
          
          {/* Hidden canvas for capturing */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-100 rounded-b-lg">
          {!capturedImage ? (
            <div className="flex justify-center gap-4">
              {devices.length > 1 && (
                <Button
                  variant="outline"
                  onClick={switchCamera}
                  disabled={isLoading || error}
                  data-testid="switch-camera"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  Switch Camera
                </Button>
              )}
              <Button
                onClick={capturePhoto}
                disabled={isLoading || error}
                className="bg-teal-700 hover:bg-teal-800 px-8"
                data-testid="capture-button"
              >
                <Camera className="w-4 h-4 mr-2" />
                Capture Photo
              </Button>
            </div>
          ) : (
            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={handleRetake}
                data-testid="retake-button"
              >
                <RotateCw className="w-4 h-4 mr-2" />
                Retake
              </Button>
              <Button
                onClick={handleUsePhoto}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="use-photo-button"
              >
                <Check className="w-4 h-4 mr-2" />
                Use This Photo
              </Button>
            </div>
          )}
          
          <p className="text-center text-sm text-gray-600 mt-3">
            {capturedImage 
              ? 'Review the captured image and use it or retake if needed'
              : 'Position the delivery challan in the frame and click Capture Photo'
            }
          </p>
        </div>
      </div>
    </div>
  )
}

export default WebcamCapture
